import { LitElement, html, css, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { themeTokens, resetStyles, baseStyles } from '../../styles/tokens.css.js';
import { validateEmail, validatePhoneE164OrBlank, validateRequired, DEFAULT_PRIVACY_POLICY_URL } from '@yebo/shared';

interface FieldErrors {
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  consent?: string;
  // #793 — the three Settings-controlled fields can be REQUIRED for a venue.
  dietaryRestrictions?: string;
  seatingPreference?: string;
  occasion?: string;
}

/** #793 — the venue's per-field choice, forwarded from the public /config. */
type FieldState = 'off' | 'optional' | 'required';

@customElement('yebo-step-contact')
export class YeboStepContact extends LitElement {
  static styles = [
    themeTokens,
    resetStyles,
    baseStyles,
    css`
      :host {
        display: flex;
        flex-direction: column;
        height: 100%;
      }
      .form {
        display: flex;
        flex-direction: column;
        height: 100%;
      }
      .fields {
        flex: 1;
        display: flex;
        flex-direction: column;
        gap: var(--yebo-space-md);
      }
      .row {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: var(--yebo-space-md);
      }
      @media (max-width: 400px) {
        .row { grid-template-columns: 1fr; }
      }
      .field {
        display: flex;
        flex-direction: column;
      }
      .booking-when {
        margin: 0;
        color: var(--yebo-text-heading);
        font-weight: 600;
      }
      .booking-when-label {
        color: var(--yebo-text-muted);
        font-weight: 400;
      }
      textarea.input {
        min-height: 60px;
        resize: vertical;
      }
      .actions {
        display: flex;
        justify-content: space-between;
        padding-top: var(--yebo-space-md);
      }
      .btn-spinner {
        display: inline-block;
        width: 14px;
        height: 14px;
        border: 2px solid rgba(255, 255, 255, 0.3);
        border-top-color: currentColor;
        border-radius: 50%;
        animation: btn-spin 0.6s linear infinite;
        flex-shrink: 0;
      }
      @keyframes btn-spin {
        to { transform: rotate(360deg); }
      }
      @media (prefers-reduced-motion: reduce) {
        .btn-spinner { animation: none; }
      }
      .checkbox-row { display: flex; align-items: center; gap: var(--yebo-space-sm, 8px); }
      .checkbox-row input[type="checkbox"] { width: 16px; height: 16px; cursor: pointer; accent-color: var(--yebo-primary, #4f46e5); }
      .conditional-field { animation: none; }
      .consent-row { display: flex; align-items: flex-start; gap: var(--yebo-space-sm, 8px); }
      .consent-row input[type="checkbox"] { width: 16px; height: 16px; margin-top: 2px; cursor: pointer; flex-shrink: 0; accent-color: var(--yebo-primary, #4f46e5); }
      .consent-label { font-size: 13px; line-height: 1.4; color: var(--yebo-text-muted, #6b7280); cursor: pointer; }
      .consent-label a { color: var(--yebo-primary, #4f46e5); text-decoration: underline; }
    `,
  ];

  @property({ type: String, attribute: 'first-name' }) firstName = '';
  @property({ type: String, attribute: 'last-name' }) lastName = '';
  @property({ type: String }) email = '';
  @property({ type: String }) phone = '';
  @property({ type: String }) notes = '';
  @property({ type: Boolean }) loading = false;
  @property({ type: Number, attribute: 'kids-count' }) kidsCount = 0;
  @property({ type: String, attribute: 'dietary-restrictions' }) dietaryRestrictions = '';
  @property({ type: Boolean, attribute: 'high-chair-needed' }) highChairNeeded = false;
  @property({ type: String, attribute: 'dietary-details' }) dietaryDetails = '';
  @property({ type: Boolean, attribute: 'consent-given' }) consentGiven = false;
  @property({ type: String, attribute: 'privacy-policy-url' }) privacyPolicyUrl = DEFAULT_PRIVACY_POLICY_URL;

  // R10.8 [P2] — progressive disclosure of accessibility / seating / occasion.
  // SCAFFOLD: rendered only when `progressiveFieldsEnabled` (the host derives
  // this from the V2 flag) AND the matching public-config key is allowlisted —
  // surfacing any of these per-tenant requires a deliberate addition to BOTH
  // PUBLIC_WIDGET_CONFIG_KEYS and the PublicWidgetConfig type (private-by-default
  // — #267/#218/#289). The new public field is NOT added here; the lead owns
  // that change. Until then these render under the V2 flag as always-on extras.
  // TODO(View 10 · R10.8 / View 13): drive visibility per field from the
  // allowlisted config keys (e.g. config.collectAccessibility) once the lead has
  // added them to the PublicWidgetConfig allowlist.
  @property({ type: Boolean, attribute: 'progressive-fields' }) progressiveFields = false;

  // #793 — the venue's Off / Optional / Required per field (from the public
  // /config `fields` block, via the store). Default 'optional' everywhere, so
  // an older server / an untouched venue renders exactly as before.
  @property({ type: String, attribute: 'dietary-state' }) dietaryState: FieldState = 'optional';
  @property({ type: String, attribute: 'seating-state' }) seatingPreferenceState: FieldState = 'optional';
  @property({ type: String, attribute: 'occasion-state' }) occasionState: FieldState = 'optional';
  @property({ type: String, attribute: 'accessibility-needs' }) accessibilityNeeds = '';
  @property({ type: String, attribute: 'seating-preference' }) seatingPreference = '';
  @property({ type: String }) occasion = '';
  /**
   * #947 — the fixed booking time for an event whose Date & Time step was
   * skipped ("Fri 4 Dec, 11:00"), so the guest sees what they are booking.
   * Empty for every other flow — renders nothing.
   */
  @property({ type: String, attribute: 'booking-when' }) bookingWhen = '';

  @state() private _errors: FieldErrors = {};
  @state() private _touched = new Set<string>();

  // ── #793 · visibility ──────────────────────────────────────────────────────
  // OFF is absolute: the control is not rendered and its value never travels
  // (privacy minimisation — dietary is health-adjacent, DR-PRIVACY-01).
  private get _showDietary(): boolean {
    return this.dietaryState !== 'off';
  }

  // Seating / Occasion were R10.8 scaffold fields, rendered only under the V2
  // `progressive-fields` flag. That stays their OPTIONAL behaviour (unchanged).
  // REQUIRED must also force them visible: a venue that requires a field it
  // cannot see would be an unsubmittable step.
  private _showScaffoldField(fieldState: FieldState): boolean {
    if (fieldState === 'off') return false;
    return this.progressiveFields || fieldState === 'required';
  }

  private get _showSeating(): boolean {
    return this._showScaffoldField(this.seatingPreferenceState);
  }

  private get _showOccasion(): boolean {
    return this._showScaffoldField(this.occasionState);
  }

  private _validate(): boolean {
    const errors: FieldErrors = {};
    if (!validateRequired(this.firstName)) errors.firstName = 'First name is required';
    if (!validateRequired(this.email)) errors.email = 'Email is required';
    else if (!validateEmail(this.email)) errors.email = 'Enter a valid email';
    if (this.phone && !validatePhoneE164OrBlank(this.phone)) errors.phone = 'Enter a valid phone number';
    // #793 — only a field that is BOTH rendered and required can refuse: an
    // invisible field must never be able to block the step.
    if (this._showDietary && this.dietaryState === 'required' && !validateRequired(this.dietaryRestrictions)) {
      errors.dietaryRestrictions = 'Please tell us about any dietary restrictions';
    }
    if (this._showSeating && this.seatingPreferenceState === 'required' && !validateRequired(this.seatingPreference)) {
      errors.seatingPreference = 'Seating preference is required';
    }
    if (this._showOccasion && this.occasionState === 'required' && !validateRequired(this.occasion)) {
      errors.occasion = 'Occasion is required';
    }
    if (!this.consentGiven) errors.consent = 'Please accept the privacy policy to continue';
    this._errors = errors;
    return Object.keys(errors).length === 0;
  }

  /**
   * Move focus to the first field that refused, so a keyboard / screen-reader
   * guest is taken to the problem rather than left at the Continue button.
   * Defensive: `renderRoot` is undefined in the node-side unit tests.
   */
  private static _errorFocusOrder: Array<keyof FieldErrors | 'consent'> = [
    'firstName', 'lastName', 'email', 'phone',
    'dietaryRestrictions', 'seatingPreference', 'occasion', 'consent',
  ];

  private _focusFirstError() {
    const first = YeboStepContact._errorFocusOrder.find((k) => this._errors[k as keyof FieldErrors]);
    if (!first) return;
    const id = first === 'consent' ? 'consentGiven' : first;
    const el = this.renderRoot?.querySelector('[id="' + id + '"]') as HTMLElement | null;
    el?.focus?.();
  }

  // #620 - every field here is a CONTROLLED input: `.value` is bound to a
  // property that only changes after the keystroke has travelled up to the host
  // (`field-change` -> `_onFieldChange` -> `_updater`) and come back down. So
  // each keystroke rewrites the DOM value of the element being typed in.
  //
  // On a desktop that is invisible. With a mobile keyboard mid-COMPOSITION -
  // Gboard/iOS predictive text, where a word stays provisional until it is
  // committed, usually by the space bar - writing `.value` can abort the
  // composition and lose the character that ended it. That character is the
  // space.
  //
  // Matches what Creation reported. Four corrupted lines out of 189 widget
  // notes in prod, across TWO different fields:
  //     mayIbringmywellbehaveddog?
  //     Dietary restrictions: AllergytoShellfish
  //     Dietary restrictions: 1xLactoseIntolerantGuest
  // Note the capitals - Allergy-to-Shellfish, Lactose-Intolerant-Guest. The
  // keyboard auto-capitalised after each space, so the spaces WERE typed; they
  // were lost afterwards. Nobody types like that by hand. And in the same
  // booking a second line, typed after the composition settled, kept its spaces
  // ("outside if weather permits") - which rules out anything server-side, since
  // no transport strips one line and not its neighbour.
  //
  // The fix is to stop feeding state back into the field while a composition is
  // open. Desktop typing fires no composition events, so that path is unchanged.
  private _composing = false;

  private _onCompositionStart() {
    this._composing = true;
  }

  private _onCompositionEnd(field: string, e: Event) {
    this._composing = false;
    // The composition's final text is in the DOM but was never dispatched, so
    // publish it now - otherwise the last word would be dropped.
    this._publish(field, (e.target as HTMLInputElement).value);
  }

  private _publish(field: string, value: string) {
    this._touched.add(field);
    this.dispatchEvent(new CustomEvent('field-change', {
      detail: { field, value },
      bubbles: true,
      composed: true,
    }));
  }

  private _onInput(field: string, e: Event) {
    // `isComposing` is the per-event flag; `_composing` covers browsers that
    // fire input between compositionstart and compositionend without setting it.
    if (this._composing || (e as InputEvent).isComposing) return;
    this._publish(field, (e.target as HTMLInputElement).value);
  }

  private _onBlur(field: string) {
    // Safety net: if compositionend never arrives (focus leaves mid-word, or a
    // keyboard misbehaves), flush what is actually in the DOM so a suppressed
    // keystroke can never be lost outright.
    if (this._composing) {
      this._composing = false;
      // Attribute selector rather than `#id` + CSS.escape: `CSS` is not defined
      // in every environment this class is constructed in (the widget's own
      // tests run in node), and these field names are internal constants, so
      // there is nothing to escape.
      const el = this.renderRoot?.querySelector(
        '[id="' + field + '"]',
      ) as HTMLInputElement | null;
      if (el) this._publish(field, el.value);
    }
    this._touched.add(field);
    this._validate();
    this.requestUpdate();
  }

  private _goBack() {
    this.dispatchEvent(new CustomEvent('step-back', { bubbles: true, composed: true }));
  }

  private _onConsentChange(e: Event) {
    const checked = (e.target as HTMLInputElement).checked;
    this._touched.add('consent');
    // Unlike other fields, consent has no blur to re-validate on (it's a
    // checkbox) and `this.consentGiven` doesn't reflect `checked` yet at this
    // point in the call stack (it's a parent-owned property, updated async via
    // the dispatched event below) - so clear the stale error directly off the
    // local `checked` value rather than re-running _validate(). Found live
    // 2026-07-26: the "Please accept the privacy policy" error kept showing
    // after ticking the box, even though submitting worked fine underneath.
    if (checked && this._errors.consent) {
      const { consent, ...rest } = this._errors;
      this._errors = rest;
    }
    this.dispatchEvent(new CustomEvent('field-change', {
      detail: { field: 'consentGiven', value: checked },
      bubbles: true,
      composed: true,
    }));
  }

  private _submit(e: Event) {
    e.preventDefault();
    this._touched = new Set([
      'firstName', 'lastName', 'email', 'phone', 'consent',
      'dietaryRestrictions', 'seatingPreference', 'occasion',
    ]);
    if (!this._validate()) {
      this._focusFirstError();
      return;
    }
    this.dispatchEvent(new CustomEvent('step-continue', { bubbles: true, composed: true }));
  }

  private static _autoComplete: Record<string, string> = {
    firstName: 'given-name',
    lastName: 'family-name',
    email: 'email',
    phone: 'tel',
  };

  private _renderField(name: keyof FieldErrors, label: string, type: string, value: string, required = false) {
    const error = this._touched.has(name) ? this._errors[name] : undefined;
    const errorId = `${name}-error`;
    return html`
      <div class="field">
        <label class="label" for=${name}>${required ? `${label} *` : label}</label>
        <input
          class="input ${error ? 'error' : ''}"
          id=${name}
          type=${type}
          .value=${value}
          @input=${(e: Event) => this._onInput(name, e)}
          @compositionstart=${() => this._onCompositionStart()}
          @compositionend=${(e: Event) => this._onCompositionEnd(name, e)}
          @blur=${() => this._onBlur(name)}
          ?required=${required}
          autocomplete=${YeboStepContact._autoComplete[name] ?? name}
          aria-required=${required ? 'true' : 'false'}
          aria-invalid=${error ? 'true' : 'false'}
          aria-describedby=${error ? errorId : nothing}
        />
        ${error ? html`<span class="field-error" id=${errorId} role="alert">${error}</span>` : ''}
      </div>
    `;
  }

  /**
   * #793 — one renderer for the three Settings-controlled optional details
   * (plus the dietary "Please specify" follow-up). Carries the required
   * affordance in the house style: a VISIBLE asterisk in the label (never
   * colour-only) plus `aria-required`, and an inline `role="alert"` error
   * associated by `aria-describedby`, exactly as the required contact fields do.
   */
  private _renderSettingsField(opts: {
    name: 'dietaryRestrictions' | 'dietaryDetails' | 'seatingPreference' | 'occasion';
    label: string;
    value: string;
    placeholder: string;
    required?: boolean;
    ariaLabel?: string;
    wrapperClass?: string;
  }) {
    const { name, label, value, placeholder } = opts;
    const required = opts.required === true;
    const error = this._touched.has(name) ? this._errors[name as keyof FieldErrors] : undefined;
    const errorId = `${name}-error`;
    return html`
      <div class=${opts.wrapperClass ?? 'field'}>
        <label class="label" for=${name}>${required ? `${label} *` : label}</label>
        <input
          class="input ${error ? 'error' : ''}"
          type="text"
          id=${name}
          .value=${value}
          @input=${(e: Event) => this._onInput(name, e)}
          @compositionstart=${() => this._onCompositionStart()}
          @compositionend=${(e: Event) => this._onCompositionEnd(name, e)}
          @blur=${() => this._onBlur(name)}
          placeholder=${placeholder}
          autocomplete="off"
          aria-label=${opts.ariaLabel ?? nothing}
          aria-required=${required ? 'true' : 'false'}
          aria-invalid=${error ? 'true' : 'false'}
          aria-describedby=${error ? errorId : nothing}
        />
        ${error ? html`<span class="field-error" id=${errorId} role="alert">${error}</span>` : ''}
      </div>
    `;
  }

  render() {
    // #793 — Seating / Occasion share a two-column row when BOTH render; with
    // one switched off the survivor renders full-width (the fields are stacked,
    // so an omitted block simply is not there — no placeholder, no layout jump).
    const scaffoldFields = [
      this._showSeating
        ? this._renderSettingsField({
            name: 'seatingPreference',
            label: 'Seating preference',
            value: this.seatingPreference,
            placeholder: 'e.g. booth, patio, quiet',
            required: this.seatingPreferenceState === 'required',
          })
        : null,
      this._showOccasion
        ? this._renderSettingsField({
            name: 'occasion',
            label: 'Occasion',
            value: this.occasion,
            placeholder: 'e.g. birthday, anniversary',
            required: this.occasionState === 'required',
          })
        : null,
    ].filter((t) => t !== null);

    return html`
      <form class="form" @submit=${this._submit} novalidate>
        <div class="fields">
          <h2 class="step-title">Your Information</h2>
          ${this.bookingWhen
            ? html`<p class="booking-when"><span class="booking-when-label">Booking for</span> ${this.bookingWhen}</p>`
            : nothing}
          <div class="row">
            ${this._renderField('firstName', 'First name', 'text', this.firstName, true)}
            ${this._renderField('lastName', 'Last name', 'text', this.lastName)}
          </div>
          ${this._renderField('email', 'Email', 'email', this.email, true)}
          ${this._renderField('phone', 'Phone', 'tel', this.phone)}
          <div class="field">
            <label class="label" for="notes">Special requests</label>
            <textarea
              class="input"
              id="notes"
              .value=${this.notes}
              @input=${(e: Event) => this._onInput('notes', e)}
              @compositionstart=${() => this._onCompositionStart()}
              @compositionend=${(e: Event) => this._onCompositionEnd('notes', e)}
              @blur=${() => this._onBlur('notes')}
              rows="2"
            ></textarea>
          </div>
          <!--
            #793 - dietary is health-adjacent (DR-PRIVACY-01). When the venue
            has switched it OFF, neither this input nor its conditional
            follow-up is rendered, and no dietary value is submitted.
          -->
          ${this._showDietary ? this._renderSettingsField({
            name: 'dietaryRestrictions',
            label: 'Dietary restrictions',
            value: this.dietaryRestrictions,
            placeholder: 'e.g. vegetarian, nut allergy',
            required: this.dietaryState === 'required',
          }) : nothing}
          ${this._showDietary && this.dietaryRestrictions.trim().length > 0
            ? this._renderSettingsField({
                name: 'dietaryDetails',
                label: 'Please specify',
                value: this.dietaryDetails,
                placeholder: 'Tell us more about your dietary needs',
                ariaLabel: 'Please specify (dietary details)',
                wrapperClass: 'field conditional-field',
              })
            : nothing}
          ${this.kidsCount > 0 ? html`
            <div class="checkbox-row">
              <input
                type="checkbox"
                id="highChairNeeded"
                .checked=${this.highChairNeeded}
                @change=${(e: Event) => {
                  this.dispatchEvent(new CustomEvent('field-change', {
                    detail: { field: 'highChairNeeded', value: String((e.target as HTMLInputElement).checked) },
                    bubbles: true, composed: true
                  }));
                }}
              />
              <label for="highChairNeeded">High chair needed?</label>
            </div>
          ` : nothing}
          ${this.progressiveFields ? html`
            <!-- R10.8 [P2] SCAFFOLD — progressive accessibility needs. NOT one
                 of the #792 Settings-controlled fields, so it is unchanged. -->
            <div class="field conditional-field">
              <label class="label" for="accessibilityNeeds">Accessibility needs</label>
              <input
                class="input"
                type="text"
                id="accessibilityNeeds"
                .value=${this.accessibilityNeeds}
                @input=${(e: Event) => this._onInput('accessibilityNeeds', e)}
                @compositionstart=${() => this._onCompositionStart()}
                @compositionend=${(e: Event) => this._onCompositionEnd('accessibilityNeeds', e)}
                placeholder="e.g. step-free access, wheelchair space"
                autocomplete="off"
              />
            </div>
          ` : nothing}
          ${scaffoldFields.length === 2
            ? html`<div class="row">${scaffoldFields}</div>`
            : scaffoldFields}
          <div class="consent-row">
            <input
              type="checkbox"
              id="consentGiven"
              .checked=${this.consentGiven}
              @change=${this._onConsentChange}
              aria-invalid=${this._touched.has('consent') && this._errors.consent ? 'true' : 'false'}
              aria-describedby=${this._touched.has('consent') && this._errors.consent ? 'consent-error' : nothing}
              required
            />
            <!--
              The checkbox consents to processing the SPECIAL-CATEGORY details the
              guest volunteers (dietary / accessibility) — that genuinely rests on
              consent. It deliberately no longer asks the guest to consent to the
              restaurant's CRM transfer: per ADR-2026-07-21-GHL-CONTROLLER-SYNC the
              restaurant is the responsible party and its CRM is its own system, so
              that transfer is disclosed (privacy policy §6) rather than consented to.
              Presenting it as a consent the guest grants would misdescribe the legal
              basis and imply a withdrawal right we do not honour that way.
            -->
            <label class="consent-label" for="consentGiven">
              I agree to the processing of my information — including any dietary or
              accessibility details I provide — to manage my booking, as described in the
              <a href=${this.privacyPolicyUrl} target="_blank" rel="noopener noreferrer">privacy policy</a>,
              which also explains how this restaurant handles my details in its own
              booking and CRM systems.
            </label>
          </div>
          ${this._touched.has('consent') && this._errors.consent
            ? html`<span class="field-error" id="consent-error" role="alert">${this._errors.consent}</span>`
            : ''}
        </div>
        <div class="actions">
          <button type="button" class="btn btn-secondary" @click=${this._goBack}>Back</button>
          <button type="submit" class="btn btn-primary" ?disabled=${this.loading}>
            ${this.loading ? html`<span class="btn-spinner" aria-hidden="true"></span>Submitting...` : 'Continue'}
          </button>
        </div>
      </form>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'yebo-step-contact': YeboStepContact;
  }
}
